import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Grid,
  FormControl,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableContainer,
  Chip,
  Divider,
} from "@mui/material";
import CloseSharpIcon from "@mui/icons-material/CloseSharp";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  useGetSchoolDiscountsQuery,
  useCreateDiscountMutation,
  useUpdateDiscountMutation,
  useDeleteDiscountMutation,
} from "./discountApiSlice";
import { StyledTextField, StyledSelect } from "../../customComponents/styled/styledInputs";
import { StyledLoadingButton } from "../../customComponents/styled/styledButtons";
import { StyledTableCell, StyledTableRow } from "../../customComponents/styled/styledTable";
import { StyledPaper } from "../../customComponents/styled/styledPaper";
import toast from "../../../helpers/toast";

const validationSchema = yup.object({
  name: yup.string().required("Name is required"),
  type: yup.string().oneOf(["percentage", "flat"]).required("Type is required"),
  value: yup
    .number()
    .required("Value is required")
    .positive("Value must be positive")
    .when("type", {
      is: "percentage",
      then: (schema) => schema.max(100, "Percentage cannot exceed 100"),
    }),
  description: yup.string(),
});

function ManageDiscountsDialog({ open, setOpen }) {
  const { data: discounts = [] } = useGetSchoolDiscountsQuery();
  const [createDiscount, { isLoading: creating }] = useCreateDiscountMutation();
  const [updateDiscount, { isLoading: updating }] = useUpdateDiscountMutation();
  const [deleteDiscount] = useDeleteDiscountMutation();
  const [editingDiscount, setEditingDiscount] = useState(null);

  const handleClose = () => {
    setOpen(false);
    setEditingDiscount(null);
  };

  const formik = useFormik({
    initialValues: { name: "", type: "percentage", value: "", description: "" },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      try {
        if (editingDiscount) {
          await updateDiscount({
            id: editingDiscount.id,
            name: values.name,
            type: values.type,
            value: Number(values.value),
            description: values.description || undefined,
          }).unwrap();
          toast.success("Discount updated");
        } else {
          await createDiscount({
            name: values.name,
            type: values.type,
            value: Number(values.value),
            description: values.description || undefined,
          }).unwrap();
          toast.success("Discount created");
        }
        setEditingDiscount(null);
        resetForm();
      } catch (err) {
        toast.error(err?.data?.message || "Failed to save discount");
      }
    },
  });

  function handleEdit(discount) {
    setEditingDiscount(discount);
    formik.setValues({
      name: discount.name,
      type: discount.type,
      value: discount.value,
      description: discount.description || "",
    });
  }

  function handleCancelEdit() {
    setEditingDiscount(null);
    formik.resetForm();
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this discount? It will no longer be available to apply.")) return;
    try {
      await deleteDiscount(id).unwrap();
      toast.success("Discount deleted");
    } catch {
      toast.error("Failed to delete discount");
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
        <Typography variant="h6">Manage Discounts</Typography>
        <IconButton size="small" onClick={handleClose}>
          <CloseSharpIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Grid container spacing={3}>
          {/* Create form */}
          <Grid item xs={12} md={5}>
            <StyledPaper sx={{ p: 2 }}>
              <Typography variant="formHeading" sx={{ mb: 2 }}>
                {editingDiscount ? "Edit Discount" : "New Discount"}
              </Typography>
              <Box component="form" onSubmit={formik.handleSubmit}>
                <Grid container rowSpacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="formSubHeading">NAME</Typography>
                    <StyledTextField
                      fullWidth
                      name="name"
                      placeholder="e.g. Scholarship 50%"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      error={formik.touched.name && Boolean(formik.errors.name)}
                      helperText={formik.touched.name && formik.errors.name}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="formSubHeading">TYPE</Typography>
                    <FormControl fullWidth>
                      <StyledSelect
                        name="type"
                        value={formik.values.type}
                        onChange={formik.handleChange}
                      >
                        <MenuItem value="percentage">Percentage (%)</MenuItem>
                        <MenuItem value="flat">Flat Amount (₦)</MenuItem>
                      </StyledSelect>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="formSubHeading">
                      {formik.values.type === "percentage" ? "PERCENTAGE (0–100)" : "AMOUNT (₦)"}
                    </Typography>
                    <StyledTextField
                      fullWidth
                      name="value"
                      type="number"
                      placeholder={formik.values.type === "percentage" ? "e.g. 10" : "e.g. 5000"}
                      value={formik.values.value}
                      onChange={formik.handleChange}
                      error={formik.touched.value && Boolean(formik.errors.value)}
                      helperText={formik.touched.value && formik.errors.value}
                      inputProps={{ min: 0, max: formik.values.type === "percentage" ? 100 : undefined }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="formSubHeading">DESCRIPTION (OPTIONAL)</Typography>
                    <StyledTextField
                      fullWidth
                      name="description"
                      placeholder="Internal note about this discount"
                      value={formik.values.description}
                      onChange={formik.handleChange}
                      multiline
                      rows={2}
                    />
                  </Grid>

                  <Grid item xs={12} textAlign="right" sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                    {editingDiscount && (
                      <StyledLoadingButton
                        type="button"
                        variant="outlined"
                        onClick={handleCancelEdit}
                      >
                        Cancel
                      </StyledLoadingButton>
                    )}
                    <StyledLoadingButton loading={creating || updating} type="submit">
                      {editingDiscount ? "Save Changes" : "Create Discount"}
                    </StyledLoadingButton>
                  </Grid>
                </Grid>
              </Box>
            </StyledPaper>
          </Grid>

          {/* Existing discounts list */}
          <Grid item xs={12} md={7}>
            <StyledPaper>
              <Box sx={{ p: "12px 15px" }}>
                <Typography variant="formHeading">Existing Discounts</Typography>
              </Box>
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ borderBottom: "1.5px solid lightgrey" }}>
                      <StyledTableCell>NAME</StyledTableCell>
                      <StyledTableCell>TYPE</StyledTableCell>
                      <StyledTableCell>VALUE</StyledTableCell>
                      <StyledTableCell>ACTIONS</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {discounts.length === 0 && (
                      <TableRow>
                        <StyledTableCell colSpan={4}>
                          <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                            No discounts yet. Create one above.
                          </Typography>
                        </StyledTableCell>
                      </TableRow>
                    )}
                    {discounts.map((d) => (
                      <StyledTableRow key={d.id}>
                        <StyledTableCell>
                          <Box>
                            <Typography variant="body2">{d.name}</Typography>
                            {d.description && (
                              <Typography variant="caption" color="text.secondary">
                                {d.description}
                              </Typography>
                            )}
                          </Box>
                        </StyledTableCell>
                        <StyledTableCell>
                          <Chip
                            label={d.type === "percentage" ? "%" : "₦"}
                            size="small"
                            variant="outlined"
                          />
                        </StyledTableCell>
                        <StyledTableCell>
                          {d.type === "percentage"
                            ? `${d.value}%`
                            : `₦${Number(d.value).toLocaleString()}`}
                        </StyledTableCell>
                        <StyledTableCell>
                          <Box sx={{ display: "flex" }}>
                            <IconButton size="small" onClick={() => handleEdit(d)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(d.id)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </StyledPaper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <StyledLoadingButton variant="outlined" onClick={handleClose} sx={{ textTransform: "none" }}>
          Close
        </StyledLoadingButton>
      </DialogActions>
    </Dialog>
  );
}

export default ManageDiscountsDialog;
